import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { IIdTitle, IdTitle } from "ui/Scripts/source/models/common/idTitle";
import { INumberTitle } from "ui/Scripts/source/models/common/numberTitle";
import { ApplicationPermissionType } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import { IListFilterItem, ListFilterItem } from "../../../models/common/listFilterItem";
import { ISavedListFilter } from "../../../models/common/savedListFilter";
import { ITypedOption } from "../../../models/common/typedOption";
import * as userGroupService from "../../../models/services/userGroupService";
import { IAppSessionState } from "../../../models/store/storeTypes";
import { IUnitNodePermissions } from "../../../models/unit/unitNodePermissions";
import { IUserGroupPage } from "../../../models/userGroup/userGroupPage";
import { HeaderMenuItem, NavigationMenu } from "../../app/NavigationMenu";
import { UserMenu } from "../../app/UserMenu";
import { ListFilterSaveDialog } from "../../framework/Filters/ListFilterSaveDialog";
import { PageContainer } from "../../framework/Page/PageContainer";
import { PageContent } from "../../framework/Page/PageContent";
import { PageTopBar } from "../../framework/Page/PageTopBar";
import { FilterTabs } from "../../framework/PageNavMenuItem/FilterTabs";
import { UnitPermissionDialog } from "../../unit/Unit/UnitPermissionDialog/UnitPermissionDialog";
import { UserGroupFilterDrawer } from "../UserGroupFilterDrawer";
import { UserGroupList } from "../UserGroupList";
import { IPageNavMenuItemActionsAction } from "source/components/framework/PageNavMenuItem/Actions";
import { abortManager } from "source/models/services/tokenService";

/* eslint-disable no-unused-vars */
const enum UserGroupPageAuthorizationLevelId {
    All = "",
    QrCodeUserGroup = "-1",
}
/* eslint-enable no-unused-vars */

// UserPage
export interface IUserGroupPageOwnProps {
}

export interface IUserGroupPageStateProps {
    appState: IAppSessionState;
    dataId: number;
    userGroupPage: IUserGroupPage;
}

export interface IUserGroupPageDispatchProps {
    onGetUserGroupsPage: () => void;
    onLogout: () => void;
    onSetCulture: (culture: string) => void;
    onSaveUserParameters: (parameters: ITypedOption[]) => void;
    onRefreshSessionState: () => void;
}

type UserGroupPageProp = IUserGroupPageOwnProps & IUserGroupPageStateProps & IUserGroupPageDispatchProps;

interface IUserGroupPageState {
    initialized: boolean;
    showNavPanel: boolean;
    activeCompanyId: string;
    authorizationLevelId: string;
    authorizationLevelFilters: IIdTitle[];
    sortColumn: string;
    sortOrderIsAsc: boolean;
    settingsActions: IPageNavMenuItemActionsAction[];
    // Filters
    userGroupFilterIsSet: boolean;
    showUserGroupFilterDialog: boolean;
    userGroupFilterDialogFilter: IListFilterItem;
    filters: IListFilterItem[];
    savedFilters: IListFilterItem[];
    activeFilterId: string;
    activeSavedFilterId: string;
    showListFilterSaveDialog: boolean;
    listFilterSaveDialogFilter: IListFilterItem;
    listFilterSaveDialogGeneralFilter: any;
    listFilterSaveDialogReservedNames: string[];
    listFilterSaveDialogOnGetGeneralFilter: () => any;
    // Properties
    dataId: number;
    showUnitNodePermissionsDialog: boolean;
    unitNodePermissions: IUnitNodePermissions;
}

export class UserGroupPage extends React.Component<UserGroupPageProp, IUserGroupPageState> {
    getAuthorizationLevelFilters(authorizationLevels: INumberTitle[]): IIdTitle[] {
        const result: IIdTitle[] = [];
        result.push(IdTitle.createIdTitle(UserGroupPageAuthorizationLevelId.All, "'" + Translations.All + "'"));
        if (authorizationLevels) {
            for (const authorizationLevel of authorizationLevels) {
                result.push(IdTitle.createIdTitle(authorizationLevel.number.toString(10), authorizationLevel.title));
            }
            result.push(IdTitle.createIdTitle("-1", Translations.AuthorizationLevelQrCodeUser));
        }
        return result;
    }
    
    getFilters = (): IListFilterItem[] => {
        const result: IListFilterItem[] = [];
        result.push(ListFilterItem.createListFilterItem(Translations.UserGroups, Translations.UserGroups,
            {
                authorizationLevelId: UserGroupPageAuthorizationLevelId.All
            }, "name", true));
        result.push(ListFilterItem.createListFilterItem(Translations.QrCodeUserGroups, Translations.QrCodeUserGroups,
            {
                authorizationLevelId: UserGroupPageAuthorizationLevelId.QrCodeUserGroup
            }, "name", true));
        ListFilterItem.sortListFilterItems(result);
        return result;
    };

    getSavedFilters = (props: UserGroupPageProp): IListFilterItem[] => {
        const result: IListFilterItem[] = [];
        if (!props || !props.userGroupPage) return result;
        for (const companyFilter of props.userGroupPage.companyFilters) {
            result.push(ListFilterItem.createListFilterItemFromSavedListFilter(companyFilter, true));
        }
        for (const userFilter of props.userGroupPage.userFilters) {
            result.push(ListFilterItem.createListFilterItemFromSavedListFilter(userFilter, false));
        }
        ListFilterItem.sortListFilterItems(result);
        return result;
    };

    getUserGroupFilterIsSet = (state: IUserGroupPageState): boolean => {
        return UserGroupFilterDrawer.filterIsSet(state.authorizationLevelFilters,
            state.authorizationLevelId);
    };

    getSettingsActions = (): IPageNavMenuItemActionsAction[] => {
        const props = this.props;
        const sessionState = props.appState.state;
        const result: IPageNavMenuItemActionsAction[] = [];
        if (sessionState.isCompanyAdmin) {
            result.push({ title: Translations.Permissions, onClick: this.handleOpenPermissions });
        }
        return result;
    };

    constructor(props: UserGroupPageProp) {
        super(props);
        abortManager.abortAll(); //Abort all abortable calls regardless of module
        const sessionState = props.appState.state;
        const filters = this.getFilters();
        const filterProps = ListFilterItem.getFilterProps(filters[0], "", true, this.getStateFilterPropsFromGeneralFilter);
        const state = {
            ...filterProps,
            initialized: !!props.userGroupPage,
            showNavPanel: sessionState.getShowPageNavigation(),
            activeCompanyId: sessionState.companyId,
            authorizationLevelFilters: this.getAuthorizationLevelFilters(props.userGroupPage ? props.userGroupPage.authorizationLevels : []),
            settingsActions: this.getSettingsActions(),
            // Filters
            userGroupFilterIsSet: false,
            activeFilterId: filters[0].id,
            activeSavedFilterId: "",
            filters,
            savedFilters: this.getSavedFilters(props),
            showUserGroupFilterDialog: false,
            userGroupFilterDialogFilter: null,
            showListFilterSaveDialog: false,
            listFilterSaveDialogFilter: null,
            listFilterSaveDialogGeneralFilter: null,
            listFilterSaveDialogReservedNames: [],
            listFilterSaveDialogOnGetGeneralFilter: null,
            // Properties
            dataId: props.dataId,
            showUnitNodePermissionsDialog: false,
            unitNodePermissions: null
        };
        this.state = {
            ...state,
            userGroupFilterIsSet: this.getUserGroupFilterIsSet(state)
        };
    }

    componentDidMount(): void {
        this.props.onGetUserGroupsPage();
    }

    componentDidUpdate(prevProps: UserGroupPageProp, prevState: IUserGroupPageState): void {
        const props = this.props;
        const state = this.state;
        const sessionState = props.appState.state;
        const prevCompanyIds = (prevProps.userGroupPage ? prevProps.userGroupPage.companies.map(i => i.id) : []).join(",");
        const companyIds = (props.userGroupPage ? props.userGroupPage.companies.map(i => i.id) : []).join(",");
        const prevCompanyId = prevProps.userGroupPage && prevProps.userGroupPage.companies.length > 0 ? prevProps.userGroupPage.companies[0].id : Base.emptyGuid;
        const companyId = props.userGroupPage && props.userGroupPage.companies.length > 0 ? props.userGroupPage.companies[0].id : Base.emptyGuid;
        if (prevProps.dataId === props.dataId && prevCompanyId === companyId && prevCompanyIds === companyIds) return;
        const savedFilters = this.getSavedFilters(props);
        const activeSavedFilterId = savedFilters.find(i => i.id === state.activeSavedFilterId)
            ? state.activeSavedFilterId
            : "";
        const stateChange = {
            dataId: props.dataId,
            initialized: !!props.userGroupPage,
            showNavPanel: state.initialized ? state.showNavPanel : sessionState.getShowPageNavigation(),
            activeCompanyId: companyId,
            authorizationLevelFilters: this.getAuthorizationLevelFilters(props.userGroupPage ? props.userGroupPage.authorizationLevels : []),
            savedFilters,
            activeSavedFilterId
        };
        const newState = { ...state, ...stateChange };
        this.setState({
            ...stateChange,
            userGroupFilterIsSet: this.getUserGroupFilterIsSet(newState),
            settingsActions: this.getSettingsActions(),
        });
    }

    handleToggleShowNavPanel = () => {
        this.setState({ showNavPanel: !this.state.showNavPanel });
    };

    handleChangeActiveCompanyId = async (companyId: string) => {
        if (this.state.activeCompanyId === companyId) return;
        const userGroupPageAuthorizationLevels = await userGroupService.getUserGroupPageAuthorizationLevels(companyId);
        this.setState({
            activeCompanyId: companyId,
            authorizationLevelFilters: this.getAuthorizationLevelFilters(userGroupPageAuthorizationLevels.authorizationLevels),
            authorizationLevelId: ""
        });
    };
    
    handleSetAuthorizationLevelId = (authorizationLevelId: string, checked: boolean) => {
        if (!checked) return;
        this.setState({ authorizationLevelId });
    };

    // #region Saved Filter
    getGeneralFilterFromState = (state: IUserGroupPageState): any => {
        return {
            authorizationLevelId: state.authorizationLevelId ? parseInt(state.authorizationLevelId, 10) : null
        };
    };

    getStateFilterPropsFromGeneralFilter = (generalFilter: any): any => {
        return {
            authorizationLevelId: generalFilter && !Base.isNullOrUndefined(generalFilter.authorizationLevelId) && !isNaN(generalFilter.authorizationLevelId) ? generalFilter.authorizationLevelId.toString(10) : ""
        };
    };
    
    getFilterPropsWithState = (listItemFilter: IListFilterItem) => {
        const state = this.state;
        let result = {
            authorizationLevelId: state.authorizationLevelId,
            sortColumn: state.sortColumn,
            sortOrderIsAsc: state.sortOrderIsAsc
        };
        if (listItemFilter) {
            result = ListFilterItem.getFilterProps(listItemFilter, result.sortColumn, result.sortOrderIsAsc, this.getStateFilterPropsFromGeneralFilter);
        }
        return result;
    };

    handleChangeActiveFilter = (activeFilterId: string) => {
        const state = this.state;
        const filter = state.filters.find(i => i.id === activeFilterId);
        if (!filter) return;
        const filterProps = this.getFilterPropsWithState(filter);
        const activeSavedFilterId = "";
        const newState = { ...state, ...filterProps, activeFilterId, activeSavedFilterId };
        this.setState({
            ...filterProps,
            activeFilterId,
            activeSavedFilterId: "",
            userGroupFilterIsSet: this.getUserGroupFilterIsSet(newState)
        });
    };

    handleChangeActiveSavedFilter = (activeSavedFilterId: string) => {
        const state = this.state;
        const savedFilter = state.savedFilters.find(i => i.id === activeSavedFilterId);
        if (!savedFilter) return;
        const filterProps = this.getFilterPropsWithState(savedFilter);
        const activeFilterId = "";
        const newState = { ...state, ...filterProps, activeFilterId, activeSavedFilterId };
        this.setState({
            ...filterProps,
            activeFilterId,
            activeSavedFilterId,
            userGroupFilterIsSet: this.getUserGroupFilterIsSet(newState)
        });
    };

    handleRemoveSavedFilter = async (savedFilterId: string) => {
        const state = this.state;
        const savedFilter = state.savedFilters.find(i => i.id === savedFilterId);
        if (!savedFilter) return;
        const result = await ListFilterSaveDialog.removeListFilterFromDb(state.savedFilters, state.activeCompanyId, savedFilterId, savedFilter.isShared, ApplicationPermissionType.UserGroup);
        if (!result) return;
        const savedFilters = state.savedFilters.filter(i => i.id !== savedFilterId);
        let activeFilterId = "";
        let activeSavedFilterId = "";
        let newfilter: IListFilterItem = null;
        if (savedFilters.length > 0) {
            newfilter = savedFilters[0];
            activeSavedFilterId = newfilter.id;
        } else if (state.filters.length > 0) {
            newfilter = state.filters[0];
            activeFilterId = newfilter.id;
        }
        const filterProps = this.getFilterPropsWithState(newfilter);
        const newState = { ...state, ...filterProps, activeFilterId, activeSavedFilterId, savedFilters };
        this.setState({
            ...filterProps,
            showUserGroupFilterDialog: false,
            activeFilterId,
            activeSavedFilterId,
            savedFilters,
            userGroupFilterIsSet: this.getUserGroupFilterIsSet(newState)
        });
    };

    handleFilterSortData = (sortColumn: string, sortOrderIsAsc: boolean) => {
        this.setState({
            sortColumn,
            sortOrderIsAsc,
        });
    };

    updateFiltersAfterSave = (orgFilterId: string, savedListFilter: ISavedListFilter, isShared: boolean) => {
        if (!savedListFilter) return;
        const state = this.state;
        const savedFilters = state.savedFilters.filter(i => i.id !== orgFilterId || i.isShared !== isShared);
        const filter = ListFilterItem.createListFilterItemFromSavedListFilter(savedListFilter, isShared);
        savedFilters.push(filter);
        ListFilterItem.sortListFilterItems(savedFilters);
        this.setState({
            showListFilterSaveDialog: false,
            savedFilters,
            activeFilterId: "",
            activeSavedFilterId: savedListFilter.id,
            userGroupFilterDialogFilter: filter
        });
    };

    getActiveFilter = (): IListFilterItem => {
        const state = this.state;
        return state.activeFilterId
            ? state.filters.find(i => i.id === state.activeFilterId)
            : state.savedFilters.find(i => i.id === state.activeSavedFilterId);
    };

    handleSaveListFilter = async (saveAs: boolean, onGetGeneralFilter: () => any) => {
        const state = this.state;
        const filter = this.getActiveFilter();
        if (!filter) return;
        if (!saveAs) {
            const savedListFilter = await ListFilterSaveDialog.saveListFilterToDb(filter, this.getGeneralFilterFromState(state), state.sortColumn, state.sortOrderIsAsc, state.activeCompanyId, ApplicationPermissionType.UserGroup);
            this.updateFiltersAfterSave(filter.id, savedListFilter, filter.isShared);
        } else {
            this.setState({
                showListFilterSaveDialog: true,
                listFilterSaveDialogFilter: filter,
                listFilterSaveDialogReservedNames: state.savedFilters.map(i => i.id).concat(state.filters.map(i => i.title)),
                listFilterSaveDialogGeneralFilter: this.getGeneralFilterFromState(state),
                listFilterSaveDialogOnGetGeneralFilter: onGetGeneralFilter
            });
        }
    };

    handleSaveListFilterDialogOk = (savedListFilter: ISavedListFilter, isShared: boolean) => {
        this.updateFiltersAfterSave("", savedListFilter, isShared);
    };

    handleSaveListFilterDialogCancel = () => {
        this.setState({
            showListFilterSaveDialog: false
        });
    };
    // #endregion Saved Filter

    // #region UserGroup Filter
    handleOpenUserGroupFilterDialog = () => {
        const filter = this.getActiveFilter();
        if (!filter) return;
        this.setState({
            showUserGroupFilterDialog: true,
            userGroupFilterDialogFilter: filter
        });
    };

    onUserGroupFilterDialogOk = (closeDialog: boolean, authorizationLevelId: string): any => {
        const newState = { ...this.state, authorizationLevelId };
        this.setState({
            showUserGroupFilterDialog: !closeDialog,
            authorizationLevelId,
            userGroupFilterIsSet: this.getUserGroupFilterIsSet(newState)
        });
        return this.getGeneralFilterFromState(newState);
    };
    
    onUserGroupFilterDialogCancel = () => {
        this.setState({ showUserGroupFilterDialog: false });
    };
    // #endregion UserGroup Filter

    handleOpenPermissions = async () => {
        const result = await AppUtils.callService(() => userGroupService.getUnitNodePermissions(this.state.activeCompanyId), false);
        if (!result) return;
        this.setState({
            showUnitNodePermissionsDialog: true,
            unitNodePermissions: result
        });
    };

    handlePermissionsDialogOk = () => {
        this.setState({
            dataId: Base.getRandomIntInteger(1000000),
            showUnitNodePermissionsDialog: false
        });
    };

    handlePermissionsDialogCancel = () => {
        this.setState({
            showUnitNodePermissionsDialog: false
        });
    };

    render() {
        const props = this.props;
        const state = this.state;
        const sessionState = props.appState.state;
        const companies = props.userGroupPage ? props.userGroupPage.companies : [];
        return (
            <PageContainer>
                <NavigationMenu
                    appState={props.appState}
                    activeMenuItem={HeaderMenuItem.UserGroups}
                    companies={companies}
                    activeCompanyId={state.activeCompanyId}
                    onChangeActiveCompany={this.handleChangeActiveCompanyId}
                />
                <PageContent>
                    <PageTopBar>
                        <UserMenu
                            appState={props.appState}
                            onLogout={props.onLogout}
                            onSetCulture={props.onSetCulture}
                            onRefreshSessionState={props.onRefreshSessionState}
                        />
                    </PageTopBar>
                    <UserGroupList
                        filterName={state.activeSavedFilterId
                            ? state.savedFilters.find(i => i.id === state.activeSavedFilterId)?.title
                            : state.activeFilterId && state.activeFilterId !== Translations.UserGroups
                                ? state.filters.find(i => i.id === state.activeFilterId)?.title
                                : null}
                        sessionState={sessionState}
                        dataId={state.dataId.toString(10)}
                        preloaded={true}
                        waitingPreloadedData={true}
                        list={props.userGroupPage ? props.userGroupPage.list : null}
                        listFilterIsSet={state.userGroupFilterIsSet}
                        authorizationLevelIds={[state.authorizationLevelId]}
                        companyId={state.activeCompanyId}
                        sortColumn={state.sortColumn}
                        sortOrderIsAsc={state.sortOrderIsAsc}
                        onRefreshSessionState={props.onRefreshSessionState}
                        onSetSortData={this.handleFilterSortData}
                        settingsActions={state.settingsActions}
                        onToggleSettingsDialog={this.handleOpenPermissions}
                        onOpenFiltersDialog={this.handleOpenUserGroupFilterDialog}
                        listFilterIsQrCodeFilter={state.activeFilterId === Translations.QrCodeUserGroups}
                        onChangeToQrCodeFilter={() => this.handleChangeActiveFilter(Translations.QrCodeUserGroups)}
                        reportTabs={
                            <FilterTabs
                                items={state.savedFilters}
                                standardFilters={state.filters}
                                selectedId={this.state.activeFilterId}
                                selectedSavedId={this.state.activeSavedFilterId}
                                onSetActiveItem={this.handleChangeActiveSavedFilter}
                                onSetActiveStandardItem={this.handleChangeActiveFilter}
                            />
                        }
                    />
                </PageContent>
                {state.showUnitNodePermissionsDialog &&
                    <UnitPermissionDialog
                        companyId={state.activeCompanyId}
                        unitNodePermissions={state.unitNodePermissions}
                        onSave={userGroupService.saveUnitNodePermissions}
                        onOk={this.handlePermissionsDialogOk}
                        onCancel={this.handlePermissionsDialogCancel}
                    />
                }
                <UserGroupFilterDrawer
                    open={state.showUserGroupFilterDialog}
                    filter={state.userGroupFilterDialogFilter}
                    authorizationLevelFilters={state.authorizationLevelFilters}
                    authorizationLevelId={state.authorizationLevelId}
                    canRemoveSharedFilters={sessionState.isCompanyAdmin}
                    onSaveFilter={this.handleSaveListFilter}
                    onRemoveFilter={this.handleRemoveSavedFilter}
                    onOk={this.onUserGroupFilterDialogOk}
                    onCancel={this.onUserGroupFilterDialogCancel}
                />
                {state.showListFilterSaveDialog &&
                    <ListFilterSaveDialog
                        sessionState={sessionState}
                        companyId={state.activeCompanyId}
                        applicationPermissionType={ApplicationPermissionType.UserGroup}
                        listFilter={state.listFilterSaveDialogFilter}
                        generalFilter={state.listFilterSaveDialogGeneralFilter}
                        sortColumn={state.sortColumn}
                        sortOrderIsAsc={state.sortOrderIsAsc}
                        reservedNames={state.listFilterSaveDialogReservedNames}
                        onGetGeneralFilter={state.listFilterSaveDialogOnGetGeneralFilter}
                        onOk={this.handleSaveListFilterDialogOk}
                        onCancel={this.handleSaveListFilterDialogCancel}
                    />
                }
            </PageContainer>
        );
    }
}